import { useApolloClient, useMutation } from '@apollo/client';
import type { DeepPartialNullable } from '@openx/types/api';
import type { PackageUpdateParams } from '@openx/types/gqlTypes';
import type { UsePackageUpdate } from '@openx/types/package/PackageHookTypes';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CreatePackageDocument } from '../../../graphql/mutation/packages/CreatePackage.generated';
import { UpdatePackageDocument } from '../../../graphql/mutation/packages/UpdatePackage.generated';
import { NestedErrorCode } from '../../../modules/graphql/types';
import { useLocalErrorHandling } from '../../../modules/graphql/useLocalErrorHandling';

export const usePackageUpdate: UsePackageUpdate = ({ pckgUid, onError }) => {
  const [updatePackageMutation, { loading }] = useMutation(UpdatePackageDocument);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const apolloClient = useApolloClient();

  const errorCallback = useCallback(
    error => {
      if (error.code === NestedErrorCode.VALIDATION_ERROR && error.message) {
        enqueueSnackbar(`Package update failed. ${error.message}`, { variant: 'error' });
        return true;
      }
      return false;
    },
    [enqueueSnackbar]
  );

  useLocalErrorHandling(CreatePackageDocument, errorCallback);

  const updatePackage = async (pckgDiff: DeepPartialNullable<PackageUpdateParams>) => {
    const { data } = await updatePackageMutation({
      onCompleted: data => {
        apolloClient.cache.evict({ fieldName: 'packages', id: 'ROOT_QUERY' });
        apolloClient.cache.evict({ fieldName: 'packageById', id: 'ROOT_QUERY' });
        apolloClient.cache.evict({ fieldName: 'openxCuratedPackages', id: 'ROOT_QUERY' });
        enqueueSnackbar(
          t('Package "{packageName}" was successfully updated', {
            packageName: data.modifyPackage.name,
          }),
          { variant: 'success' }
        );
      },
      onError: error => {
        const errorMessages = error.graphQLErrors.reduce((_prev: string[], curr) => {
          const firstErrorMessage = curr?.extensions?.errors?.[0]?.message;
          return firstErrorMessage ? [firstErrorMessage] : [];
        }, []);

        onError?.(errorMessages[0] || t('Failed to update package'));
        enqueueSnackbar(t('Failed to update package'), { variant: 'error' });
      },
      variables: { id: pckgUid, package: pckgDiff as PackageUpdateParams },
    });

    if (!data) {
      throw new Error(t('Something went wrong updating package!'));
    }

    return data?.modifyPackage;
  };

  return { isUpdating: loading, updatePackage };
};
